import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {SortableContext, useSortable, verticalListSortingStrategy} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {
    FolderOpen, FolderInput, Globe, User, Trash2, ChevronRight, ChevronDown,
    Pencil, ArrowRightLeft, Plus, MoreHorizontal, Clock, Hash,
} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {formatDate} from '@/lib/date-utils'
import {Button} from '@/components/ui/button'
import {TableCell, TableRow} from '@/components/ui/table'
import {MutedText} from '@/components/shared/MutedText'
import {Strong} from '@/components/shared/Strong'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {IconText} from '@/components/shared/IconText'
import {DragHandle} from '@/components/shared/DragHandle'
import {IndentedTableCell} from '@/components/shared/IndentedTableCell'
import {SortableTableRow} from '@/components/shared/SortableTableRow'
import {TreeNodeDescription} from '@/components/shared/TreeNodeDescription'
import {Badge} from '@/components/ui/badge'
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {toast} from '@/hooks/use-toast'
import type {Page} from '@/types/page'
import {CategoryType, type ReportCategoryListItem, type ReportDefinitionListItem} from './types'
import {ReportRow} from './ReportRow'

/**
 * Self-contained, recursive tree node for the categories table.
 * Fetches its own children and reports when expanded.
 */
export function CategoryRow({collection, depth, expandedIds, onToggle, canManageGlobal, onRename, onTransfer, onMove, onDelete, onMoveQuery, sortable = false}: {
    collection: ReportCategoryListItem
    depth: number
    expandedIds: Set<string>
    onToggle: (id: string) => void
    canManageGlobal: boolean
    onRename: (c: ReportCategoryListItem) => void
    onTransfer: (c: ReportCategoryListItem) => void
    onMove: (c: ReportCategoryListItem) => void
    onDelete: (c: ReportCategoryListItem) => void
    onMoveQuery: (query: ReportDefinitionListItem) => void
    sortable?: boolean
}) {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const isExpanded = expandedIds.has(collection.categoryId)
    const canModify = collection.type === CategoryType.GLOBAL ? canManageGlobal : true
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        id: collection.categoryId,
        /* eslint-disable-next-line i18next/no-literal-string -- DnD item type identifier */
        data: {type: 'category', parentId: collection.parentCategoryId ?? null, version: collection.version},
        disabled: !sortable,
    })

    const rowStyle = sortable ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    } : undefined

    // Fetch child categories when expanded
    const {data: childrenPage} = useQuery<Page<ReportCategoryListItem>>({
        queryKey: ['reporting-category-children', collection.categoryId],
        queryFn: () => services.reporting.categories({parentId: collection.categoryId, size: 100}) as Promise<Page<ReportCategoryListItem>>,
        enabled: isExpanded,
    })

    // Fetch reports when expanded
    const {data: reports} = useQuery<ReportDefinitionListItem[]>({
        queryKey: ['reporting-category-reports', collection.categoryId],
        queryFn: () => services.reporting.categoryReports(collection.categoryId),
        enabled: isExpanded,
    })

    const children = childrenPage?.content ?? []
    const reportList = reports ?? []

    const deleteReportMutation = useMutation({
        mutationFn: (id: string) => services.reporting.deleteReport(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['reporting-category-reports', collection.categoryId]})
            queryClient.invalidateQueries({queryKey: ['reporting-categories']})
            queryClient.invalidateQueries({queryKey: ['reporting-category-children']})
            toast({title: i18n.reporting.reportDeleted as string})
        },
        onError: (error: Error) => {
            toast({variant: 'destructive', description: error.message})
        },
    })

    const hasContent = children.length > 0 || reportList.length > 0

    return (
        <>
            <SortableTableRow
                ref={setNodeRef}
                dragStyle={rowStyle}
                clickable
                alternateBg={depth % 2 !== 0}
                onClick={() => onToggle(collection.categoryId)}
            >
                <IndentedTableCell
                    depth={depth}
                    sortable={sortable}
                    tone={
                        depth !== 0
                            ? 'innerNode'
                            : collection.type === CategoryType.GLOBAL
                                ? 'globalRoot'
                                : 'personalRoot'
                    }
                >
                    <InlineRow gap="md">
                        {sortable && (
                            <DragHandle
                                {...attributes}
                                {...listeners}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`drag-category-${collection.categoryId}`}
                            />
                        )}
                        <Icon icon={isExpanded ? ChevronDown : ChevronRight} size="xs" muted className="shrink-0"/>
                        <Icon icon={FolderOpen} size="xs" muted className="shrink-0"/>
                        <Strong truncate data-testid={`category-name-${collection.categoryId}`}>{collection.name}</Strong>
                    </InlineRow>

                    {collection.description && (
                        <TreeNodeDescription>{collection.description}</TreeNodeDescription>
                    )}
                </IndentedTableCell>
                <TableCell>
                    <Badge variant="outline" className="min-w-[80px] justify-center">
                        <IconText
                            icon={collection.type === CategoryType.GLOBAL ? Globe : User}
                            size="xs"
                        >
                            {collection.type === CategoryType.GLOBAL ? i18n.reporting.typeGlobal : i18n.reporting.typePersonal}
                        </IconText>
                    </Badge>
                </TableCell>
                <TableCell className="text-right">
                    <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button data-testid={`btn-actions-${collection.categoryId}`} variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="h-4 w-4"/>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                                    <IconText icon={Hash} size="xs">{collection.reportCount} {i18n.reporting.reportsLabel}</IconText>
                                </DropdownMenuItem>
                                <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                                    <IconText icon={Clock} size="xs">{formatDate(collection.updatedAt)}</IconText>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator/>
                                {canModify && (
                                    <DropdownMenuItem data-testid={`btn-new-report-${collection.categoryId}`} onClick={() => navigate(routes.reporting.reports.new(collection.categoryId))}>
                                        <Icon icon={Plus} className="mr-2"/>{i18n.reporting.newReport}
                                    </DropdownMenuItem>
                                )}
                                {canModify && (
                                    <DropdownMenuItem data-testid={`btn-rename-category-${collection.categoryId}`} onClick={() => onRename(collection)}>
                                        <Icon icon={Pencil} className="mr-2"/>{i18n.reporting.renameCategory}
                                    </DropdownMenuItem>
                                )}
                                {canModify && (
                                    <DropdownMenuItem data-testid={`btn-move-category-${collection.categoryId}`} onClick={() => onMove(collection)}>
                                        <Icon icon={FolderInput} className="mr-2"/>{i18n.reporting.moveCategory}
                                    </DropdownMenuItem>
                                )}
                                {canManageGlobal && (
                                    <DropdownMenuItem data-testid={`btn-transfer-category-${collection.categoryId}`} onClick={() => onTransfer(collection)}>
                                        <Icon icon={ArrowRightLeft} className="mr-2"/>{i18n.reporting.transferCategory}
                                    </DropdownMenuItem>
                                )}
                                {canModify && (
                                    <>
                                        <DropdownMenuSeparator/>
                                        <DropdownMenuItem data-testid={`btn-delete-category-${collection.categoryId}`} className="text-destructive" onClick={() => onDelete(collection)}>
                                            <Icon icon={Trash2} className="mr-2"/>{i18n.reporting.deleteCategory}
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </TableCell>
            </SortableTableRow>

            {/* Expanded: child categories (recursive) + reports */}
            {isExpanded && (
                <>
                    {/* Child categories as indented table rows — sortable */}
                    <SortableContext items={children.map(c => c.categoryId)} strategy={verticalListSortingStrategy}>
                        {children.map(child => (
                            <CategoryRow
                                key={child.categoryId}
                                collection={child}
                                depth={depth + 1}
                                expandedIds={expandedIds}
                                onToggle={onToggle}
                                canManageGlobal={canManageGlobal}
                                onRename={onRename}
                                onTransfer={onTransfer}
                                onMove={onMove}
                                onDelete={onDelete}
                                onMoveQuery={onMoveQuery}
                                sortable={sortable}
                            />
                        ))}
                    </SortableContext>

                    {/* Reports as proper table rows at child depth — sortable */}
                    <SortableContext items={reportList.map(r => r.reportDefinitionId)} strategy={verticalListSortingStrategy}>
                        {reportList.map(report => (
                            <ReportRow
                                key={report.reportDefinitionId}
                                report={report}
                                depth={depth + 1}
                                canModify={canModify}
                                onDelete={() => deleteReportMutation.mutate(report.reportDefinitionId)}
                                onMove={() => onMoveQuery(report)}
                                isDeleting={deleteReportMutation.isPending}
                                sortable={sortable}
                            />
                        ))}
                    </SortableContext>

                    {/* Empty state when expanded but nothing inside */}
                    {!hasContent && (
                        <TableRow>
                            <IndentedTableCell
                                depth={depth + 1}
                                basePadding={16}
                                colSpan={3}
                                tone="mutedBackground"
                            >
                                <MutedText size="sm" block>{i18n.reporting.noReports}</MutedText>
                            </IndentedTableCell>
                        </TableRow>
                    )}
                </>
            )}
        </>
    )
}
